"use client";

import React, { useEffect, useState } from "react";
import ManagementHeader from "../../../components/ManagementHeader";
import { supabase } from "../../../supabaseClient";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

export default function Complaints() {
  const { isEnabled } = useFeatureFlags();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // -1 left, 1 right

  const [confirmArchive, setConfirmArchive] = useState(false);
  const [view, setView] = useState("active");
  const [message, setMessage] = useState(null);
  const [savingComplaint, setSavingComplaint] = useState(false);

  useEffect(() => {
    loadComplaints();
  }, [view]);

  const loadComplaints = async () => {
    setLoading(true);

    let query = supabase
      .from("complaints")
      .select("*")
      .order("submitted_at", { ascending: false });

    query = view === "archived"
      ? query.eq("archived", true)
      : query.eq("archived", false);

    const { data, error } = await query;

    if (error) {
      console.error(error);
      setMessage({
        type: "error",
        text: "Unable to load complaints.",
      });
    } else {
      setComplaints(data || []);
      setCurrentIndex(0);
    }

    setLoading(false);
  };

  const handleArchive = async (id) => {
    setMessage(null);

    const { data: { user } = {} } = await supabase.auth.getUser();
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("complaints")
      .update({
        archived: true,
        archived_at: now,
        archived_by: user?.id || null,
        updated_at: now,
      })
      .eq("id", id);

    if (error) {
      console.error(error);
      setMessage({ type: "error", text: "Failed to archive complaint." });
      return;
    }

    setConfirmArchive(false);
    setMessage({
      type: "success",
      text: "Complaint archived. The record has been retained for audit and history.",
    });
    await loadComplaints();
  };

  const handleRestore = async (id) => {
    setMessage(null);
    const { error } = await supabase
      .from("complaints")
      .update({
        archived: false,
        archived_at: null,
        archived_by: null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error(error);
      setMessage({ type: "error", text: "Failed to restore complaint." });
      return;
    }

    setMessage({ type: "success", text: "Complaint restored to the active complaints list." });
    await loadComplaints();
  };


  const updateCurrentComplaintField = (field, value) => {
    setComplaints((current) =>
      current.map((complaint, index) =>
        index === currentIndex ? { ...complaint, [field]: value } : complaint,
      ),
    );
  };

  const saveComplaintTracking = async () => {
    if (!currentComplaint) return;
    setSavingComplaint(true);
    setMessage(null);

    const { error } = await supabase
      .from("complaints")
      .update({
        status: currentComplaint.status || "Submitted",
        management_response: currentComplaint.management_response || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", currentComplaint.id);

    if (error) {
      console.error(error);
      setMessage({ type: "error", text: "Complaint progress could not be saved." });
    } else {
      setMessage({ type: "success", text: "Complaint progress saved. Linked patients can now see the updated status in their portal." });
      await loadComplaints();
    }

    setSavingComplaint(false);
  };

  const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  };

  const goPrevious = () => {
    if (currentIndex === 0) return;
    setDirection(-1);
    setConfirmArchive(false);
    setCurrentIndex((i) => i - 1);
  };

  const goNext = () => {
    if (currentIndex === complaints.length - 1) return;
    setDirection(1);
    setConfirmArchive(false);
    setCurrentIndex((i) => i + 1);
  };

  const currentComplaint = complaints[currentIndex];

  return (
    <div className="flex flex-col min-h-screen">
      <ManagementHeader />

      <main className="nhsuk-width-container nhsuk-u-padding-4">
        <h1 className="nhsuk-heading-l">Complaints management</h1>

        <p className="nhsuk-body">
          Review complaint progress and retain a complete audit history. Complaints can be archived, but never permanently deleted.
        </p>

        <div className="nhsuk-button-group nhsuk-u-margin-bottom-4" role="group" aria-label="Complaint view">
          <button
            type="button"
            className={`nhsuk-button ${view === "active" ? "" : "nhsuk-button--secondary"}`}
            onClick={() => { setView("active"); setConfirmArchive(false); }}
          >
            Active complaints
          </button>
          <button
            type="button"
            className={`nhsuk-button ${view === "archived" ? "" : "nhsuk-button--secondary"}`}
            onClick={() => { setView("archived"); setConfirmArchive(false); }}
          >
            Archived complaints
          </button>
        </div>

        {message && (
          <div
            className={`nhsuk-inset-text ${
              message.type === "success" ? "border-green-600" : "border-red-600"
            }`}
          >
            <span className="nhsuk-u-visually-hidden">
              {message.type === "success" ? "Success" : "Error"}:
            </span>
            <p>{message.text}</p>
          </div>
        )}

        {loading && <p className="nhsuk-body">Loading complaints…</p>}

        {!loading && complaints.length === 0 && (
          <p className="nhsuk-body">
            There are no {view === "archived" ? "archived" : "active"} complaints to display.
          </p>
        )}

        {!loading && currentComplaint && (
          <>
            {/* Navigation bar */}
            <div className="flex items-center justify-between nhsuk-u-margin-bottom-4">
              <button
                type="button"
                className="nhsuk-button nhsuk-button--secondary"
                onClick={goPrevious}
                disabled={currentIndex === 0}
              >
                ← Previous
              </button>

              <p className="nhsuk-body-s nhsuk-u-margin-bottom-0">
                Complaint {currentIndex + 1} of {complaints.length}
              </p>

              <button
                type="button"
                className="nhsuk-button nhsuk-button--secondary"
                onClick={goNext}
                disabled={currentIndex === complaints.length - 1}
              >
                Next →
              </button>
            </div>

            {/* Slider window */}
            <div
              style={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                key={currentComplaint.id}
                className="nhsuk-card"
                style={{
                  transform:
                    direction === 0
                      ? "translateX(0)"
                      : direction === 1
                        ? "translateX(10px)"
                        : "translateX(-10px)",
                  animation: "slideIn 220ms ease",
                }}
              >
                <div className="nhsuk-card__content">
                  <h2 className="nhsuk-heading-m">Complaint received</h2>

                  <dl className="nhsuk-summary-list nhsuk-u-margin-bottom-4">
                    <div className="nhsuk-summary-list__row">
                      <dt className="nhsuk-summary-list__key">Submitted</dt>
                      <dd className="nhsuk-summary-list__value">
                        {formatDateTime(currentComplaint.submitted_at)}
                      </dd>
                    </div>

                    {currentComplaint.archived && (
                      <div className="nhsuk-summary-list__row">
                        <dt className="nhsuk-summary-list__key">Archived</dt>
                        <dd className="nhsuk-summary-list__value">
                          {formatDateTime(currentComplaint.archived_at)}
                        </dd>
                      </div>
                    )}

                    <div className="nhsuk-summary-list__row">
                      <dt className="nhsuk-summary-list__key">Reporter</dt>
                      <dd className="nhsuk-summary-list__value">
                        {currentComplaint.reporter_name ||
                          "Anonymous / not provided"}
                      </dd>
                    </div>

                    <div className="nhsuk-summary-list__row">
                      <dt className="nhsuk-summary-list__key">
                        Complaint about
                      </dt>
                      <dd className="nhsuk-summary-list__value">
                        {currentComplaint.complained_about || "Not provided"}
                      </dd>
                    </div>
                  </dl>

                  <h3 className="nhsuk-heading-s">Complaint details</h3>

                  <div className="nhsuk-inset-text">
                    <p
                      className="nhsuk-body"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {currentComplaint.complaint_text ||
                        "No details provided."}
                    </p>
                  </div>

                  <div className="pguh-complaint-tracking-panel">
                    <h3 className="nhsuk-heading-s">Patient-visible complaint progress</h3>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="complaint-status">Status</label>
                      <select
                        id="complaint-status"
                        className="nhsuk-select"
                        value={currentComplaint.status || "Submitted"}
                        onChange={(event) => updateCurrentComplaintField("status", event.target.value)}
                      >
                        <option>Submitted</option>
                        <option>Acknowledged</option>
                        <option>Investigating</option>
                        <option>Response issued</option>
                        <option>Closed</option>
                      </select>
                    </div>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="complaint-management-response">Management response</label>
                      <textarea
                        id="complaint-management-response"
                        className="nhsuk-textarea"
                        rows="5"
                        value={currentComplaint.management_response || ""}
                        onChange={(event) => updateCurrentComplaintField("management_response", event.target.value)}
                        placeholder="Optional response shown to a linked patient portal account"
                      />
                    </div>
                    <button
                      type="button"
                      className="nhsuk-button"
                      onClick={saveComplaintTracking}
                      disabled={savingComplaint}
                    >
                      {savingComplaint ? "Saving…" : "Save complaint progress"}
                    </button>
                  </div>

                  {view === "active" && !confirmArchive && (
                    <button
                      type="button"
                      className="nhsuk-button nhsuk-button--secondary"
                      onClick={() => setConfirmArchive(true)} disabled={!isEnabled("feature.management_complaint_archiving")}
                    >
                      Archive complaint
                    </button>
                  )}

                  {view === "active" && confirmArchive && (
                    <div className="nhsuk-warning-callout nhsuk-u-margin-top-4">
                      <h3 className="nhsuk-warning-callout__label">
                        <span role="text">
                          <span className="nhsuk-u-visually-hidden">Important:</span>
                          Confirm archive
                        </span>
                      </h3>
                      <p className="nhsuk-body">
                        This complaint will move to the archived complaints list. Nothing will be deleted and the full record will remain available for audit and history.
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          className="nhsuk-button"
                          onClick={() => handleArchive(currentComplaint.id)} disabled={!isEnabled("feature.management_complaint_archiving")}
                        >
                          Yes, archive complaint
                        </button>
                        <button
                          type="button"
                          className="nhsuk-button nhsuk-button--secondary"
                          onClick={() => setConfirmArchive(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {view === "archived" && (
                    <button
                      type="button"
                      className="nhsuk-button nhsuk-button--secondary"
                      onClick={() => handleRestore(currentComplaint.id)}
                    >
                      Restore complaint
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Simple slide animation */}
            <style jsx>{`
              @keyframes slideIn {
                from {
                  opacity: 0;
                  transform: translateX(${direction === 1 ? "40px" : "-40px"});
                }
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
            `}</style>
          </>
        )}
      </main>
    </div>
  );
}
